import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import {
	Col,
	Form,
	FormGroup,
	Modal,
	ModalBody,
	Row,
} from 'reactstrap';

import { Button } from '../../../../../_shared';
import { messages } from '../../../../../_shared/i18n/messages';

import { CONTINUATION_TYPES, FIELD_TYPES } from '../../../../constants';
import FormField from '../../../FormField';
import { formSectionDataShape } from '../../utils/propTypes';
import FormNavigationOptionsDropdown, { FORM_ITEM_TYPE_ADVANCED }
	from '../FormEditorField/components/FormNavigationOptionsDropdown';

const propTypes = {

	/**
	 * The ID of the current form section to retreive the relevant settings.
	 */
	currentFormSection: formSectionDataShape.isRequired,

	/**
	 * The list of form sections.
	 */
	sections: PropTypes.arrayOf(formSectionDataShape).isRequired,

	/**
	 * True, if the modal is currently visible.
	 */
	isOpen: PropTypes.bool.isRequired,

	/**
	 * Callback function to handle the changes to the advanced setting.
	 */
	onChange: PropTypes.func.isRequired,

	/**
	 * Callback function to handle the user setting an
	 * advanced continuation type for the section.
	 */
	onSave: PropTypes.func.isRequired,

	/**
	 * Callback function to handle the user cancelling the modal.
	 */
	onCancel: PropTypes.func.isRequired,

};

/**
 * Modal to display advanced settings for product ticket
 * and product ticket group continuation types for form sections.
 *
 * For each product ticket and ticket group, the editor can customize
 * the proceeding form section or prompt the user to submit the form.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object} The component to render.
 */
const AdvancedSettingsModal = (props) => {

	const {
		currentFormSection,
		sections,
		isOpen,
		onChange,
		onCancel,
		onSave,
	} = props;

	const intl = useIntl();

	let defaultObjectType = CONTINUATION_TYPES.PRODUCT_TICKETS;

	// Set the default object type to be the continuation type of the existing
	// section if it is an advanced continuation type
	if (currentFormSection.continuation_type === CONTINUATION_TYPES.PRODUCT_TICKETS ||
		currentFormSection.continuation_type === CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS) {

		defaultObjectType = currentFormSection.continuation_type;

	}

	// Keep track of the active continuation type for the current section
	const [objectType, setObjectType] = useState(defaultObjectType);

	// Get the settings that apply to the current form section
	const sectionAdvancedSettings = currentFormSection.advanced_settings;

	const ticketAdvancedSettings = sectionAdvancedSettings.filter((setting) =>
		setting.object_type === CONTINUATION_TYPES.PRODUCT_TICKETS);

	const ticketGroupAdvancedSettings = sectionAdvancedSettings.filter((setting) =>
		setting.object_type === CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS);

	return (
		<Modal
			className="form-section-advanced-settings"
			size="lg"
			isOpen={isOpen}
			toggle={onCancel}>
			<ModalBody>
				<Form>
					<FormGroup>
						<FormField
							className="advanced-setting-type"
							type={FIELD_TYPES.RADIO}
							name="object_type"
							value={objectType}
							options={[
								{
									id: CONTINUATION_TYPES.PRODUCT_TICKETS,
									label: intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_SECTION_ADVANCED_SETTINGS_TICKETS),
									value: CONTINUATION_TYPES.PRODUCT_TICKETS
								},
								{
									id: CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS,
									label: intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_SECTION_ADVANCED_SETTINGS_TICKET_GROUPS),
									value: CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS
								},
							]}
							label={
								<h4>
									<FormattedMessage {...messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_SECTION_ADVANCED_SETTINGS_TITLE} />
								</h4>
							}
							isEditing
							onChange={(name, value) => setObjectType(value)} />
					</FormGroup>
					<FormGroup>
						{objectType === CONTINUATION_TYPES.PRODUCT_TICKETS &&
							ticketAdvancedSettings.map((ticketAdvancedSetting) => (
								<Row
									key={ticketAdvancedSetting.id}
									className="p-2">
									<Col className="p-2 d-flex align-items-center">
										<b>{ticketAdvancedSetting.title}</b>
										{!ticketAdvancedSetting.active &&
											<i className="ms-1">
												{`(${intl.formatMessage(messages.TC_COMPONENTS_GENERAL_INACTIVE_LABEL)})`}
											</i>
										}
									</Col>
									<Col className="p-2">
										<FormNavigationOptionsDropdown
											formItem={ticketAdvancedSetting}
											formItemType={FORM_ITEM_TYPE_ADVANCED}
											sections={sections}
											onChange={onChange} />
									</Col>
								</Row>
							))
						}
						{objectType === CONTINUATION_TYPES.PRODUCT_TICKETS &&
							ticketAdvancedSettings.length === 0 &&
								<b>
									<FormattedMessage
										{...messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_SECTION_ADVANCED_SETTINGS_TICKETS_EMPTY} />
								</b>
						}
						{objectType === CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS &&
							ticketGroupAdvancedSettings.map((ticketGroupAdvancedSetting) => (
								<Row
									key={ticketGroupAdvancedSetting.id}
									className="p-2">
									<Col className="p-2 d-flex align-items-center">
										<b>{ticketGroupAdvancedSetting.title}</b>
									</Col>
									<Col className="p-2">
										<FormNavigationOptionsDropdown
											formItem={ticketGroupAdvancedSetting}
											formItemType={FORM_ITEM_TYPE_ADVANCED}
											sections={sections}
											onChange={onChange} />
									</Col>
								</Row>
							))
						}
						{objectType === CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS &&
							ticketGroupAdvancedSettings.length === 0 &&
								<b>
									<FormattedMessage
										{...messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_SECTION_ADVANCED_SETTINGS_TICKET_GROUPS_EMPTY} />
								</b>
						}
					</FormGroup>
					<Button
						className="me-2"
						onClick={() => onSave(objectType)}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL} />
					</Button>
				</Form>
			</ModalBody>
		</Modal>
	);

};

AdvancedSettingsModal.propTypes = propTypes;

export default AdvancedSettingsModal;
